import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Download, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { useAssetGenerator } from '@/hooks/activation';
import { PackEmailSignatureSectionProps } from '@/types/activation/activation-pack';

const signatureHtmlToPlainText = (html: string): string => {
  const container = document.createElement('div');
  container.innerHTML = html;

  const lines: string[] = [];
  const heading = container.querySelector('div');
  if (heading?.textContent?.trim()) lines.push(heading.textContent.trim());

  const description = heading?.nextElementSibling?.textContent?.trim();
  if (description) lines.push(description);

  container.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
    const label = link.textContent?.replace(/\s+/g, ' ').trim();
    if (label) lines.push(`${label}: ${link.href}`);
  });

  return [...new Set(lines)].join('\n');
};

const copyPlainText = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) throw new Error('Clipboard access is unavailable');
};

export const PackEmailSignatureSection = ({
  pack,
  brand = '#4E9CFF',
}: PackEmailSignatureSectionProps) => {
  const { resolveAsset } = useAssetGenerator(pack);
  const [html, setHtml] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSignature = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const asset = await resolveAsset('email_signature_html');
      if (!asset || typeof asset.data !== 'string' || !asset.data.trim()) {
        throw new Error('The email signature could not be generated.');
      }
      setHtml(asset.data);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not build the signature.');
    } finally {
      setIsLoading(false);
    }
  }, [resolveAsset]);

  useEffect(() => {
    void loadSignature();
  }, [loadSignature]);

  const handleCopy = async () => {
    const plainText = signatureHtmlToPlainText(html);
    try {
      if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
            }),
          ]);
          toast.success('Signature copied — paste it into your email settings');
        } catch {
          await copyPlainText(plainText);
          toast.success('Plain-text signature copied');
        }
      } else {
        await copyPlainText(plainText);
        toast.success('Plain-text signature copied');
      }

      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error('Couldn\'t copy the signature');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${pack.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'genie'}-email-signature.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!pack.touchpoints.email_signature) return null;

  return (
    <OverviewSectionCard
      title="Email signature"
      subtitle="Put your Genie in every email with a ready-to-paste, branded signature widget."
      genieIcon="mail"
      brand={brand}
    >
      <div className="space-y-5">
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Preview
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={!html || isLoading} className="h-9">
                <Download className="mr-1.5 h-4 w-4" />
                HTML
              </Button>
              <Button
                size="sm"
                onClick={handleCopy}
                disabled={!html || isLoading}
                className="h-9 border-0 text-white"
                style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
              >
                {isCopied ? (
                  <Check className="mr-1.5 h-4 w-4" />
                ) : (
                  <Copy className="mr-1.5 h-4 w-4" />
                )}
                {isCopied ? 'Copied' : 'Copy signature'}
              </Button>
            </div>
          </div>

          <div className="min-h-32 overflow-x-auto rounded-xl border border-gray-200 bg-white p-5">
            {isLoading ? (
              <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" style={{ color: brand }} />
                Building your signature…
              </div>
            ) : error ? (
              <div className="flex min-h-24 flex-col items-center justify-center gap-3 text-center">
                <p className="text-sm text-red-600">{error}</p>
                <Button variant="outline" size="sm" onClick={() => void loadSignature()}>
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                  Try again
                </Button>
              </div>
            ) : (
              <div
                className="pointer-events-none inline-block min-w-max"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            )}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">Gmail</div>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              Settings → See all settings → General → Signature, then paste.
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">Outlook</div>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              Settings → Accounts → Signatures, then paste.
            </p>
          </div>
        </div>
      </div>
    </OverviewSectionCard>
  );
};
